<script lang="ts" setup>
import type { MiniAppAccountChoiceItem } from '@repo/api/web/models'

const {
  accountChoice,
  isSelectingAccount,
  isRetrying,
  selectAccount,
  retry,
} = usePlatformSessionGate()

const titleRef = ref<HTMLElement | null>(null)
const selectedUserId = ref<number | null>(null)

const accounts = computed(() => accountChoice.value?.accounts ?? [])
const hasAccounts = computed(() => accounts.value.length > 0)

const statusMessage = computed(() => {
  if (!hasAccounts.value) {
    return isRetrying.value ? 'Повторный вход…' : 'Не удалось войти'
  }
  return isSelectingAccount.value ? 'Входим…' : 'Выберите аккаунт'
})

function accountLabel(account: MiniAppAccountChoiceItem): string {
  const full = account.fullName?.trim()
  if (full) {
    return full
  }
  const name = account.name?.trim()
  if (name) {
    return name
  }
  return `Аккаунт ${account.userId}`
}

async function onSelect(account: MiniAppAccountChoiceItem) {
  if (isSelectingAccount.value) {
    return
  }
  selectedUserId.value = account.userId
  await selectAccount(account.userId)
}

onMounted(() => {
  titleRef.value?.focus()
})
</script>

<template lang="html">
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-default p-6 pt-[max(1.5rem,env(safe-area-inset-top))] pb-[max(1.5rem,env(safe-area-inset-bottom))] pl-[max(1.5rem,env(safe-area-inset-left))] pr-[max(1.5rem,env(safe-area-inset-right))]"
    role="main"
    data-testid="platform-account-choice"
  >
    <div class="flex w-full max-w-sm flex-col gap-4">
      <!-- Empty choice: fail closed with retry (PlatformGate pattern). -->
      <template v-if="!hasAccounts">
        <div class="flex flex-col items-center gap-2 text-center">
          <UIcon
            :name="isRetrying ? 'i-lucide-loader-circle' : 'i-lucide-circle-alert'"
            class="size-11 shrink-0 mb-2 text-[22px]"
            :class="{
              'animate-spin motion-reduce:animate-none': isRetrying,
            }"
          />
          <h1
            ref="titleRef"
            tabindex="-1"
            class="text-highlighted text-pretty font-medium text-base outline-none"
          >
            Не удалось войти
          </h1>
          <p class="text-sm text-muted text-pretty">
            Список аккаунтов пуст. Попробуйте ещё раз. Если ошибка повторяется,
            откройте магазин из Telegram или VK.
          </p>
        </div>

        <UButton
          label="Повторить"
          icon="i-lucide-refresh-cw"
          color="neutral"
          variant="solid"
          block
          size="lg"
          :loading="isRetrying"
          :disabled="isRetrying"
          data-testid="platform-account-choice-retry"
          @click="retry"
        />
      </template>

      <template v-else>
        <div class="flex flex-col items-center gap-2 text-center">
          <UIcon
            :name="isSelectingAccount ? 'i-lucide-loader-circle' : 'i-lucide-users'"
            class="size-11 shrink-0 mb-2 text-[22px]"
            :class="{
              'animate-spin motion-reduce:animate-none': isSelectingAccount,
            }"
          />
          <h1
            ref="titleRef"
            tabindex="-1"
            class="text-highlighted text-pretty font-medium text-base outline-none"
          >
            Выберите аккаунт
          </h1>
          <p class="text-sm text-muted text-pretty">
            С этим Telegram связано несколько аккаунтов. Выберите, под каким
            аккаунтом продолжить.
          </p>
        </div>

        <ul
          class="flex flex-col gap-2"
          aria-label="Аккаунты"
        >
          <li
            v-for="account in accounts"
            :key="account.userId"
          >
            <UButton
              color="neutral"
              variant="soft"
              block
              size="lg"
              class="justify-start"
              :loading="isSelectingAccount && selectedUserId === account.userId"
              :disabled="isSelectingAccount"
              :data-testid="`platform-account-choice-item-${account.userId}`"
              @click="onSelect(account)"
            >
              <span class="flex min-w-0 flex-col items-start gap-0.5 text-left">
                <span class="truncate font-medium">
                  {{ accountLabel(account) }}
                </span>
                <span class="text-xs text-muted tabular-nums">
                  ID {{ account.userId }}
                </span>
              </span>
            </UButton>
          </li>
        </ul>
      </template>

      <span
        class="sr-only"
        role="status"
        aria-live="polite"
        :aria-busy="isSelectingAccount || isRetrying"
      >
        {{ statusMessage }}
      </span>
    </div>
  </div>
</template>
